import React from "react";
import { graphql, HeadProps, useStaticQuery } from "gatsby";
import { IntlProvider, useIntl } from "gatsby-plugin-intl";
import { Thing, WithContext } from "schema-dts";
import { useSiteUrl } from "../../hooks/useSiteUrl";

type HeadSeoProps = {
  headProps: HeadProps;
  title?: string;
  metaDescription?: string;
  metaKeywords?: string;
  canonicalUrl?: string;
  structuredData?: WithContext<Thing>;
  children?: React.ReactNode;
};

function HeadSeoWithIntl({
  title,
  metaDescription,
  metaKeywords,
  canonicalUrl,
  structuredData,
  children,
  headProps,
}: HeadSeoProps) {
  const { locale, formatMessage } = useIntl();
  const siteUrl = useSiteUrl();
  const { location } = headProps;

  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);

  if (!canonicalUrl) {
    canonicalUrl = `${siteUrl}${location.pathname}`;
  }

  title = title
    ? `${title} | ${data.site.siteMetadata.title}`
    : data.site.siteMetadata.title;

  return (
    <>
      <html lang={locale} />
      <title>{title}</title>

      <meta
        name="description"
        content={metaDescription || formatMessage({ id: "meta.description" })}
      />
      <meta
        name="keywords"
        content={metaKeywords || formatMessage({ id: "meta.keywords" })}
      />

      <meta property="og:site_name" content="Userbrain" />
      <meta
        property="og:title"
        content="Fast, simple, affordable user testing."
      />
      <meta
        property="og:description"
        content={metaDescription || formatMessage({ id: "meta.description" })}
      />
      <meta
        property="og:image"
        content="https://www.userbrain.com/og_image.png"
      />
      <meta property="og:url" content="https://www.userbrain.com/en/" />
      <meta property="og:type" content="website" />
      <meta
        name="twitter:title"
        content="Fast, simple, affordable user testing."
      />
      <meta
        name="twitter:description"
        content={metaDescription || formatMessage({ id: "meta.description" })}
      />
      <meta
        name="twitter:image"
        content="https://www.userbrain.com/og_image.png"
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content="@userbrain" />
      <meta name="twitter:site" content="@userbrain" />
      <meta name="twitter:image:alt" content="Userbrain" />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {children}
    </>
  );
}

export function HeadSeo(props: HeadSeoProps) {
  const pageContextIntl = (
    props.headProps.pageContext as unknown as {
      intl: { messages: Record<string, string>; language: "en" };
    }
  ).intl;

  return (
    <IntlProvider
      locale={pageContextIntl.language}
      messages={pageContextIntl.messages}
    >
      <HeadSeoWithIntl {...props} />
    </IntlProvider>
  );
}
